import { useApplicationBaseCurrency } from "../../../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import { Flex, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";

import type { DataType, ServiceTableRow } from "../..";
import {
  ApplicationCalculationModel,
  ApplicationCalculationSegmentModel,
  ApplicationUtils,
  ServiceCostApplicationCalculationModel,
} from "../../../../../../viewModels/Applications/ApplicationModel";
import formatPrice from "../../../../../../utils/formatPrice";
import TableUtils from "../../../../../../utils/TableUtils";

type TableSummaryProps = {
  data: readonly DataType[];
  creator: ApplicationCalculationModel["creator"];
  calculationSegments: Array<ApplicationCalculationSegmentModel>;
};

const TableSummary = React.memo(({ data, creator, calculationSegments }: TableSummaryProps) => {
  const services: Array<ServiceCostApplicationCalculationModel> = data.filter((x): x is ServiceTableRow => x.rowType === "service");

  const { t } = useTranslation("translations");

  const baseCompanyCurrency = useApplicationBaseCurrency();

  const hasOtherCurrency = Boolean(services.find((x) => x.currencyId !== baseCompanyCurrency.id));
  const hasVat = ApplicationUtils.servicesHasVat(services);

  const cost = services.reduce<number>((acc, x) => {
    if (x.costInBaseCurrency) {
      return acc + x.costInBaseCurrency;
    }
    return acc;
  }, 0);

  const withoutVatCost = services.reduce<number>((acc, x) => {
    if (x.costInBaseCurrency) {
      return acc + TableUtils.calculateVats({ cost: x.costInBaseCurrency, vat: x.vat }).withoutVat;
    }
    return acc;
  }, 0);

  const vatCost = services.reduce<number>((acc, x) => {
    if (x.costInBaseCurrency) {
      return acc + TableUtils.calculateVats({ cost: x.costInBaseCurrency, vat: x.vat }).vat;
    }
    return acc;
  }, 0);

  const costForClient = services.reduce<number>((acc, x) => {
    if (x.costForClientInBaseCurrency) {
      return acc + x.costForClientInBaseCurrency;
    }
    return acc;
  }, 0);

  const withoutVatCostForClient = services.reduce<number>((acc, x) => {
    if (x.costForClientInBaseCurrency) {
      return acc + TableUtils.calculateVats({ cost: x.costForClientInBaseCurrency, vat: x.vat }).withoutVat;
    }
    return acc;
  }, 0);

  const vatCostForClient = services.reduce<number>((acc, x) => {
    if (x.costForClientInBaseCurrency) {
      return acc + TableUtils.calculateVats({ cost: x.costForClientInBaseCurrency, vat: x.vat }).vat;
    }
    return acc;
  }, 0);

  const transitFrom = calculationSegments.reduce<number>((acc, x) => {
    if (x.deliveryTimeFrom) {
      return acc + x.deliveryTimeFrom;
    }

    return acc;
  }, 0);

  const transitTo = calculationSegments.reduce<number>((acc, x) => {
    if (x.deliveryTimeTo) {
      return acc + x.deliveryTimeTo;
    }

    return acc;
  }, 0);

  const profit = costForClient - cost;

  return (
    <>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={10}>
          <Flex style={{ padding: 6 }} vertical gap={6}>
            <Typography.Text strong>{hasOtherCurrency ? t("total_currency_with_currency") : t("total_sum")}</Typography.Text>

            {hasVat && (
              <Flex vertical>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("total_without_vat")}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {" "}
                  {t("total_vat")}
                </Typography.Text>
              </Flex>
            )}
          </Flex>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1}>
          <Flex style={{ padding: 6 }} vertical gap={6}>
            <Typography.Text strong>{formatPrice(cost, baseCompanyCurrency.symbol)}</Typography.Text>
            {hasVat && (
              <Flex vertical>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatPrice(withoutVatCost, baseCompanyCurrency.symbol)}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatPrice(vatCost, baseCompanyCurrency.symbol)}
                </Typography.Text>
              </Flex>
            )}
          </Flex>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} />
        <Table.Summary.Cell index={2}>
          <Flex style={{ padding: 6 }} vertical gap={6}>
            <Typography.Text strong>{formatPrice(costForClient, baseCompanyCurrency.symbol)}</Typography.Text>
            {hasVat && (
              <Flex vertical>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatPrice(withoutVatCostForClient, baseCompanyCurrency.symbol)}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatPrice(vatCostForClient, baseCompanyCurrency.symbol)}
                </Typography.Text>
              </Flex>
            )}
          </Flex>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2}>
          <Flex style={{ padding: 6, height: "100%" }} vertical>
            <Typography.Text strong>{formatPrice(profit, baseCompanyCurrency.symbol)}</Typography.Text>
            {hasVat && <div style={{ height: 43 }} />}
          </Flex>
        </Table.Summary.Cell>
      </Table.Summary.Row>

      <Table.Summary.Row>
        <Table.Summary.Cell colSpan={14} index={0}>
          <div style={{ padding: "2px 6px" }}>
            <Typography.Text strong>{t("profitability")}: </Typography.Text>
            {cost !== 0 && <Typography.Text type={profit > 0 ? "success" : "danger"}>{Math.ceil((profit / cost) * 100)}%</Typography.Text>}
          </div>
        </Table.Summary.Cell>
      </Table.Summary.Row>

      <Table.Summary.Row>
        <Table.Summary.Cell colSpan={14} index={0}>
          <div style={{ padding: "2px 6px" }}>
            <Typography.Text strong>
              {t("creator")}: {creator.name}
            </Typography.Text>
          </div>
        </Table.Summary.Cell>
      </Table.Summary.Row>

      <Table.Summary.Row>
        <Table.Summary.Cell colSpan={14} index={0}>
          <div style={{ padding: "2px 6px" }}>
            <Typography.Text strong>{t("total_delivery_date", { from: transitFrom, to: transitTo })}</Typography.Text>
          </div>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </>
  );
});

export default TableSummary;
